import type { IconName } from "../components/Icon";
import type { SearchResultType } from "@atlas/contracts";

/** Domains with a real list/detail read-write view. */
export type ListDomain = "books" | "media" | "places";

const LIST_DOMAINS: ListDomain[] = ["books", "media", "places"];

export function isListDomain(value: string | undefined): value is ListDomain {
  return !!value && (LIST_DOMAINS as string[]).includes(value);
}

export const DOMAIN_LABELS: Record<ListDomain | "finance" | "projects" | "shopping", string> = {
  books: "کتاب‌ها",
  media: "فیلم و سریال",
  places: "مکان‌ها",
  finance: "مالی",
  projects: "پروژه‌ها",
  shopping: "لیست خرید",
};

export const SEARCH_TYPE_LABELS: Record<SearchResultType, string> = {
  book: "کتاب",
  media: "فیلم/سریال",
  place: "مکان",
  wishlist: "خرید",
  project: "پروژه",
  relationship: "رابطه",
};

export type NavId = "home" | ListDomain | "more";

export interface NavItem {
  id: NavId;
  label: string;
  path: string;
  icon: IconName;
}

/** Exactly five bottom-nav items — Finance/Projects/Shopping live under "بیشتر". */
export const NAV_ITEMS: NavItem[] = [
  { id: "home", label: "خانه", path: "/", icon: "home" },
  { id: "books", label: DOMAIN_LABELS.books, path: "/books", icon: "book" },
  { id: "media", label: DOMAIN_LABELS.media, path: "/media", icon: "film" },
  { id: "places", label: DOMAIN_LABELS.places, path: "/places", icon: "map" },
  { id: "more", label: "بیشتر", path: "/more", icon: "more-horizontal" },
];

export type Route =
  | { name: "home" }
  | { name: "books"; id?: string }
  | { name: "media"; id?: string }
  | { name: "places"; id?: string }
  | { name: "more" }
  | { name: "finance" }
  | { name: "projects" }
  | { name: "shopping" };

function safeDecodeURIComponent(encoded: string): string {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

export function parseRoute(path: string): Route {
  const parts = path.split("/").filter(Boolean).map(safeDecodeURIComponent);
  if (parts.length === 0) return { name: "home" };

  const [section, sub] = parts;
  if (section === "books" || section === "media" || section === "places") {
    return { name: section, id: sub };
  }
  if (section === "more") {
    if (sub === "finance") return { name: "finance" };
    if (sub === "projects") return { name: "projects" };
    if (sub === "shopping") return { name: "shopping" };
    return { name: "more" };
  }
  return { name: "home" };
}

/** Which bottom-nav item should render active for a given route. */
export function navGroupFor(route: Route): NavId {
  if (route.name === "books" || route.name === "media" || route.name === "places") return route.name;
  if (route.name === "finance" || route.name === "projects" || route.name === "shopping" || route.name === "more") {
    return "more";
  }
  return "home";
}
